import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, ArrowRight, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";
import { usePageSEO } from "../hooks/usePageSEO";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    const locale = lang === "en" ? "en-GB" : "de-CH";
    return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

// Resolve cover URLs that come back from the backend as relative paths (/api/blog-image/...)
const resolveCover = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

export default function Blog() {
  const { t, lang } = useT();
  const tb = t?.blog || {};

  usePageSEO({
    title: lang === "en"
      ? "Blog — AI trading, automated trading & passive income | Alphalane"
      : "Blog — KI-Trading, automatisiertes Trading & passives Einkommen | Alphalane",
    description: lang === "en"
      ? "Alphalane Blog: Clear articles on AI trading, automated trading, passive income and risk management. Practical knowledge for modern investors."
      : "Alphalane Blog: Verständliche Artikel zu KI-Trading, automatisiertem Trading, passivem Einkommen und Risikomanagement. Praxiswissen für moderne Anleger.",
    canonical: "https://alphalane-tradingsystems.com/blog",
  });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const localize = (post) => {
    if (lang !== "en") return { title: post.title, excerpt: post.excerpt };
    const tr = post.en_translation;
    return {
      title: (tr && tr.title) || post.title,
      excerpt: (tr && tr.excerpt) || post.excerpt,
    };
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <span className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">{tb.tag || "Blog"}</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/about" className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 font-medium">
              {tb.about || "About"}
            </Link>
            <Link to="/" className="al-cta-ghost !py-2 !px-4 text-sm">
              <ArrowLeft className="h-4 w-4" /> {tb.home || "Home"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-3xl">
          <span className="al-tag">{tb.tag || "Blog"}</span>
          <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {tb.h1a || "Wissen für moderne "}<span className="al-grad-text">{tb.h1b || "Anleger."}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            {tb.sub || "Praxiswissen zu KI-Trading, automatisiertem Trading, passivem Einkommen und Risikomanagement — verständlich erklärt, ohne Bullshit."}
          </p>
        </div>

        {loading ? (
          <div className="mt-14 al-card p-12 text-center text-slate-500">{tb.loading || "Lade Artikel…"}</div>
        ) : posts.length === 0 ? (
          <div className="mt-14 al-card p-12 text-center text-slate-500">{tb.empty || "Noch keine Artikel."}</div>
        ) : (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => {
              const cover = resolveCover(p.cover);
              const { title, excerpt } = localize(p);
              return (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="al-card overflow-hidden group flex flex-col"
                >
                  {cover && (
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      <img
                        src={cover}
                        alt={title}
                        loading="lazy"
                        className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(p.date, lang)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_time}</span>
                    </div>
                    <h2 className="mt-3 font-display font-semibold text-xl leading-snug">{title}</h2>
                    <p className="mt-3 text-[14px] text-slate-600 leading-relaxed flex-1">{excerpt}</p>
                    <div className="mt-5 flex items-center gap-1.5 text-[var(--al-purple)] text-sm font-display font-semibold">
                      {tb.readMore || "Weiterlesen"} <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-[var(--al-line)] bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Alphalane Trading Systems</div>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-slate-900">{lang === "en" ? "Imprint" : "Impressum"}</Link>
            <Link to="/datenschutz" className="hover:text-slate-900">{lang === "en" ? "Privacy" : "Datenschutz"}</Link>
            <Link to="/agb" className="hover:text-slate-900">{lang === "en" ? "Terms" : "AGB"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
